import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { IconLock, IconPhone, IconEye, IconEyeOff, IconBolt } from "@tabler/icons-react";
import { notifications } from "@/lib/notifications";
import { useAuthStore } from "../../../store/authStore";
import GoogleButton from "../../../components/auth/GoogleButton";
import { hasGoogleHint } from "../../../utils/googleHint";
import authApi from "../../../services/authApi";
import { useTranslation } from "../../../i18n/hooks/useTranslation";
import styles from "./LoginPage.module.scss";
import LanguageSwitcher from "../../../components/common/LanguageSwitcher";
import Logo from "../../../components/common/Logo";
import SideImage from "../../../components/auth/SideImage";

// One-click demo sign-in — LOCAL DEVELOPMENT ONLY. Shown only under `vite dev`, on localhost, with both values set in the git-ignored
// .env.development.local. A production build has no DEV branch and no such env file, so neither this nor the values can ship; it goes
// through the normal login request, so the server has no special path for it.
const DEV_DEMO = import.meta.env.DEV
  && ["localhost", "127.0.0.1"].includes(window.location.hostname)
  && import.meta.env.VITE_DEV_DEMO_LOGIN
  && import.meta.env.VITE_DEV_DEMO_PASSWORD
  ? { phone: import.meta.env.VITE_DEV_DEMO_LOGIN, password: import.meta.env.VITE_DEV_DEMO_PASSWORD, rememberMe: true }
  : null;

const inputCls =
  "w-full px-3 py-2 border border-zinc-300 dark:border-zinc-600 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 bg-white dark:bg-zinc-700 dark:text-zinc-100 placeholder:text-zinc-400 dark:placeholder:text-zinc-400";

// After this many consecutive wrong phone/password attempts on the SAME number we lock
// the button. The backend returns the same generic error whether the number is
// unregistered or the password is wrong (no account enumeration), so this covers the
// "number not in the user table" case without the server ever leaking that it isn't.
const MAX_LOGIN_ATTEMPTS = 3;


function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const login = useAuthStore((state) => state.login);
  const googleAuth = useAuthStore((state) => state.googleAuth);
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    phone: "",
    password: "",
    rememberMe: false,
  });
  const [errors, setErrors] = useState({});
  // Set when the backend blocks this device (ACCESS_RESTRICTED). Disables the login
  // button so a blocked user can't spam-click; a page reload clears it.
  const [blocked, setBlocked] = useState(false);
  // Consecutive wrong phone/password failures for the number currently typed. At
  // MAX_LOGIN_ATTEMPTS the button locks (`tooManyAttempts`); editing the phone field
  // starts a fresh count.
  const [attempts, setAttempts] = useState(0);
  const [tooManyAttempts, setTooManyAttempts] = useState(false);
  const redirectTo = new URLSearchParams(location.search).get("redirect");

  const validateForm = () => {
    const newErrors = {};
    // The field accepts a phone number OR an email address (for users who verified by email).
    const identifier = formData.phone.trim();
    if (!identifier) {
      newErrors.phone = t("validation.phoneRequired");
    } else if (identifier.includes("@")) {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(identifier)) newErrors.phone = t("auth.invalidEmail");
    } else if (!/^[0-9]{11}$/.test(identifier)) {
      newErrors.phone = t("validation.invalidPhone");
    }
    const trimmedPassword = formData.password.trim();
    if (!trimmedPassword) {
      newErrors.password = t("validation.passwordRequired");
    } else if (trimmedPassword.length < 6) {
      newErrors.password = t("validation.passwordTooShort");
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!validateForm()) return;
    await submitLogin(formData);
  };

  const submitLogin = async ({ phone, password, rememberMe }) => {
    setLoading(true);
    try {
      const result = await login({
        identifier: phone.trim(),
        password: password.trim(),
        rememberMe,
      });
      if (result.user.role === "admin") {
        navigate(redirectTo || "/admin");
      } else {
        navigate(redirectTo || "/");
      }
    } catch (error) {
      // The "not verified → resend OTP + finish registration" recovery is phone/SMS-specific,
      // so only run it when the identifier is a phone number (not an email login).
      const isEmailLogin = phone.includes("@");
      if (error?.code === "PHONE_NOT_VERIFIED" && !isEmailLogin) {
        try {
          await authApi.sendOtp({ phone: phone.trim(), purpose: "registration" });
        } catch (_) {
          // OTP send failure is non-fatal — still redirect
        }
        notifications.show({
          title: t("auth.phoneVerificationRequired"),
          message: t("auth.otpSentCompleteRegistration"),
          color: "orange",
        });
        navigate("/register", {
          state: { phone: phone.trim(), fromLogin: true },
        });
      } else if (error?.code === "ACCOUNT_BLOCKED") {
        // Account-level block (User.blockedAt) comes back as a 401 — show the real
        // reason instead of the generic credentials error, and lock the button:
        // retrying with any password can't help a blocked account.
        setBlocked(true);
        notifications.show({
          title: t("common.error"),
          message: t("auth.accountBlocked"),
          color: "red",
        });
      } else {
        // Blocked device / rate-limit — the api interceptor already translated codes
        // like ACCESS_RESTRICTED into a localized error.message; surface the real
        // reason and lock the button so they can't keep hammering while restricted.
        const isRestricted = error?.code === "ACCESS_RESTRICTED" || error?.status === 429 || error?.status === 403;
        if (isRestricted) {
          setBlocked(true);
        }
        // Only genuine credential failures (400/401 with no special code) get the friendly
        // "wrong phone or password" line. Everything else — device block, rate limit,
        // network/server outage (no status at all) — shows its own translated message
        // instead of masquerading as a wrong password.
        const credentialFailure =
          (error?.status === 400 || error?.status === 401) && !error?.code;
        // Count this failure toward the per-number lock. On the MAX-th consecutive miss
        // we lock the button and nudge them to check the number / sign up (the number may
        // simply not be registered — the server won't say which).
        let locked = false;
        if (credentialFailure) {
          const next = attempts + 1;
          setAttempts(next);
          if (next >= MAX_LOGIN_ATTEMPTS) {
            setTooManyAttempts(true);
            locked = true;
          }
        }
        notifications.show({
          title: t("common.error"),
          message: locked
            ? t("auth.tooManyLoginAttempts")
            : credentialFailure || !error?.message
              ? t("auth.loginFailed")
              : error.message,
          color: "red",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
    // A different number gets a fresh attempt budget — clear the per-number lock.
    if (field === "phone") {
      setAttempts(0);
      setTooManyAttempts(false);
    }
  };

  return (
    <div className={styles.container}>
      {/* Left Side - Branding */}
      <div className={styles.brandingSection}>
        <SideImage />
      </div>

      {/* Right Side - Form */}
      <div className={styles.formSection}>
        {/* language switcher */}
        <div className={styles.languageSwitcher}>
          <LanguageSwitcher />
        </div>

        <div className={styles.formContainer}>
          {/* logo */}
          <div className={styles.logo}>
            <a href="https://sohozkaj.com/" rel="noopener noreferrer" target="_blank">
              <Logo w={150} m="auto" />
            </a>
          </div>

          <p className="text-center text-sm text-zinc-500 dark:text-zinc-400 mb-6">
            {t("auth.loginToAccount")}
          </p>

          {/* Card */}
          <div className="bg-white dark:bg-zinc-900 rounded-lg shadow-sm border border-zinc-200 dark:border-zinc-700 p-6">
            <form onSubmit={handleSubmit} noValidate>
              <div className="flex flex-col gap-4">
                {/* Phone or email */}
                <div>
                  <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
                    {t("auth.phoneOrEmail")} <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400">
                      <IconPhone size={16} />
                    </span>
                    <input
                      type="text"
                      placeholder={t("auth.phoneOrEmailPlaceholder")}
                      value={formData.phone}
                      onChange={(e) => handleChange("phone", e.target.value.replace(/^\s*\+/, ""))}
                      className={`${inputCls} pl-8 ${errors.phone ? "border-red-500 focus:ring-red-400" : ""}`}
                    />
                  </div>
                  {errors.phone && (
                    <p className="mt-1 text-xs text-red-500">{errors.phone}</p>
                  )}
                </div>

                {/* Password */}
                <div>
                  <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
                    {t("auth.password")} <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400 z-10">
                      <IconLock size={16} />
                    </span>
                    <PasswordInputWithIcon
                      value={formData.password}
                      onChange={(e) => handleChange("password", e.target.value)}
                      placeholder={t("auth.enterPassword")}
                      error={!!errors.password}
                    />
                  </div>
                  {errors.password && (
                    <p className="mt-1 text-xs text-red-500">{errors.password}</p>
                  )}
                </div>

                {/* Remember me + Forgot password */}
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={formData.rememberMe}
                      onChange={(e) => handleChange("rememberMe", e.target.checked)}
                      className="w-4 h-4 rounded border-zinc-300 text-orange-500 focus:ring-orange-400 accent-orange-500"
                    />
                    <span className="text-sm text-zinc-600 dark:text-zinc-300">
                      {t("auth.rememberMe")}
                    </span>
                  </label>
                  <Link
                    to="/forgot-password"
                    className="text-sm text-orange-500 hover:text-orange-600 font-medium"
                  >
                    {t("auth.forgotPassword")}
                  </Link>
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={loading || blocked || tooManyAttempts}
                  className="w-full px-4 py-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 disabled:cursor-not-allowed text-white rounded-md font-medium text-sm transition-colors flex items-center justify-center gap-2"
                >
                  {loading && (
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                    </svg>
                  )}
                  {t("auth.signIn")}
                </button>

                {/* Locked after too many wrong attempts on this number — the number may
                    not be registered, so point them at sign-up / editing the number. */}
                {tooManyAttempts && (
                  <p className="text-center text-xs text-red-500">
                    {t("auth.tooManyLoginAttempts")}
                  </p>
                )}
              </div>
            </form>

            {/* Google: signs in the account that holds this verified email (or creates one) */}
            {hasGoogleHint() && (
            <div className="mt-4 flex flex-col gap-3">
              <div className="flex items-center gap-3 text-xs text-zinc-400">
                <span className="h-px flex-1 bg-zinc-200 dark:bg-zinc-700" />
                {t("auth.orDivider")}
                <span className="h-px flex-1 bg-zinc-200 dark:bg-zinc-700" />
              </div>
              <GoogleButton
                label={t("auth.loginWithGoogle")}
                onToken={async (accessToken) => {
                  try {
                    const r = await googleAuth({ accessToken });
                    navigate(r.user.role === "admin" ? (redirectTo || "/admin") : r.isNew ? "/onboard" : (redirectTo || "/"));
                  } catch (err) {
                    notifications.show({ message: err?.message || t("auth.googleFailed"), color: "red" });
                  }
                }}
              />
            </div>
            )}
          </div>

          {/* Sign up link */}
          <p className="text-center mt-4 text-sm text-zinc-600 dark:text-zinc-400">
            {t("auth.dontHaveAccount")}{" "}
            <Link to="/register" className="text-orange-500 hover:text-orange-600 font-bold">
              {t("auth.signUp")}
            </Link>
          </p>

          {DEV_DEMO && (
            <div className="mt-4 rounded-xl border border-dashed border-zinc-300 p-3 dark:border-zinc-600">
              <button
                type="button"
                onClick={() => submitLogin(DEV_DEMO)}
                disabled={loading || blocked || tooManyAttempts}
                className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-zinc-200 px-4 py-2 text-sm font-semibold text-zinc-600 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-zinc-700 dark:text-zinc-400 dark:hover:bg-zinc-800"
              >
                <IconBolt size={16} className="text-primary" />
                {t("auth.devDemoLogin")}
              </button>
              <p className="mt-1.5 text-center text-xs text-zinc-400 dark:text-zinc-500">{t("auth.devDemoOnly")}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Password input with left icon slot + show/hide toggle
function PasswordInputWithIcon({ value, onChange, placeholder, error }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input
        type={show ? "text" : "password"}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={`${inputCls} pl-8 pr-10 ${error ? "border-red-500 focus:ring-red-400" : ""}`}
      />
      <button
        type="button"
        tabIndex={-1}
        onClick={() => setShow((v) => !v)}
        className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300"
      >
        {show ? <IconEyeOff size={16} /> : <IconEye size={16} />}
      </button>
    </div>
  );
}

export default LoginPage;
